import type { ReactNode } from 'react'

import Box from '@mui/material/Box'
import type { SxProps } from '@mui/material/styles'

import { getLogoElevation, getLogoShadow, LOGO_SHADOW_OFFSET } from './logoShadow'

interface LogoTileProps {
  /** Logo/brand visual (arbitrary children). */
  children: ReactNode
  /** Square size in px. */
  size?: number
  /** Surface color token. */
  bgcolor?: string
  /** Border color token. */
  borderColor?: string
  /**
   * Drop-shadow: `'offset'`/`true` = hard bottom-right (merchant, P2P);
   * `'elevation'` = soft `0 2px 8px`; `false` (default) = none.
   */
  shadow?: boolean | 'offset' | 'elevation'
  /**
   * Reserve layout space for the offset shadow (margin = shadow offset) so a
   * sibling flex `gap` clears it. Default true; ignored unless shadow is offset.
   */
  reserveShadowSpace?: boolean
  sx?: SxProps
}

/**
 * The shared rounded brand/logo tile primitive (MerchantHighlightCard,
 * P2PLogoStack, LogoClusterCard): fixed square, 1px border, optional shadow.
 * Named `LogoTile` (not `MerchantLogo`) to avoid colliding with MXUI's
 * `MerchantLogo` export.
 */
const LogoTile = ({
  bgcolor = 'background.paper',
  borderColor = 'common.white',
  children,
  reserveShadowSpace = true,
  shadow = false,
  size = 64,
  sx,
}: LogoTileProps) => {
  const isOffset = shadow === true || shadow === 'offset'
  const isElevation = shadow === 'elevation'

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor,
        borderRadius: '8px',
        display: 'flex',
        flexShrink: 0,
        height: size,
        justifyContent: 'center',
        overflow: 'hidden',
        width: size,
        border: '1px solid',
        borderColor,
        ...(isOffset && { boxShadow: getLogoShadow }),
        ...(isElevation && { boxShadow: getLogoElevation }),
        // The offset box-shadow paints outside the box; reserve its footprint so
        // a flex gap starts from the shadow's outer edge, not the tile border.
        // (The soft elevation shadow is symmetric and needs no reservation.)
        ...(isOffset &&
          reserveShadowSpace && {
            mr: `${LOGO_SHADOW_OFFSET.x}px`,
            mb: `${LOGO_SHADOW_OFFSET.y}px`,
          }),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

export default LogoTile
